import {HttpErrorResponse} from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  OnInit
} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import isEmpty from 'lodash-es/isEmpty';

import * as moment from 'moment/moment';
import {BsModalService} from 'ngx-bootstrap/modal';
import {ToastrService} from 'ngx-toastr';
import {EMPTY, from, merge, Observable, of} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  share,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';

import {RegulationTreeService} from '../+configuration/regulation-tree/regulation-tree.service';
import {AdministrationService} from '../+settings/administration/administration.service';
import {Destroyable} from '../destroyable.component';
import {CliReg, FeatureEnum} from '../models/models.interfaces';
import {CliCorePropertiesService} from '../navigation/cli-core-properties.service';
import {Data} from '../shared/api';
import {StatusVM} from '../shared/documents/documents.interface';
import {LocalizationService} from '../shared/localization/localization.service';
import {ModalService} from '../shared/modal';
import {DefaultPagination, Pagination, TreeNode} from '../shared/ui';
import {DropdownTreeviewConfig} from '../shared/ui/tree-multi-select/dropdown-treeview.component';
import {TreeItem} from '../shared/ui/tree-multi-select/treeview.component';
import {endOfDay} from '../shared/utils/date';
import {escapeValue} from '../shared/utils/strings';

import {TemplateActionModalComponent} from './template-action-modal/template-action-modal.component';
import {ETemplate} from './template-management.interfaces';
import {TemplateManagementService, templates_per_page} from './template-management.service';

@Component({
  selector: 'gp-template-management',
  templateUrl: './template-management.component.html',
  styleUrls: ['./template-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TemplateManagementComponent extends Destroyable implements OnInit, AfterViewInit {
  public static ACCESS_NAME = 'TemplateManagement';
  public loading = false;
  public templates: ETemplate[];
  public selectedETemplate: ETemplate;
  public errorMessage: string;
  public filtersForm: UntypedFormGroup;
  public pagination: Pagination = new DefaultPagination(templates_per_page);
  public newlyCreatedTemplateId: number;
  public publishForm: UntypedFormGroup;
  public trigger$: Observable<number>;
  public statusTree: TreeItem[] = [];
  public statusList: StatusVM[] = [{
    id: 1,
    select: 'draft',
    name: 'DOCUMENTS.draft'
  }, {
    id: 2,
    select: 'published',
    name: 'TEMPLATE.published'
  }, {
    id: 3,
    select: 'unpublished',
    name: 'TEMPLATE.unpublished'
  }];
  public selectedStatuses: Set<string> = new Set<string>();
  public treeConfig: DropdownTreeviewConfig = {
    isShowFilter: false,
    isShowCollapseExpand: false,
  };
  public currentRegulationId: number;
  public signatureEnabled = false;
  public regulationTree: Array<TreeNode<CliReg>> = null;
  private gloRegIdsOfRegTree: number[];
  private regulations: Array<Data<CliReg>>;

  constructor(private templateService: TemplateManagementService,
              private router: Router,
              private modalService: ModalService,
              private bsModalService: BsModalService,
              private administrationService: AdministrationService,
              private localizationService: LocalizationService,
              private regulationTreeService: RegulationTreeService,
              private fb: UntypedFormBuilder,
              private toastService: ToastrService,
              private translate: TranslateService,
              private cliCorePropertiesService: CliCorePropertiesService,
              private cdr: ChangeDetectorRef) {
    super();
    this.filtersForm = this.fb.group({
      name: [''],
      folder: [''],
      regulation: 0,
      locale: [''],
      createdBy: [''],
      date: ['']
    });
    this.initializePublishForm();
  }

  public ngOnInit(): void {
    this.cliCorePropertiesService.get()
      .pipe(
        tap((properties) => {
          this.signatureEnabled = properties.features?.includes(FeatureEnum.E_SIGNATURE) ?? false;
          this.cdr.markForCheck();
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
    this.initStatusTree();

    this.getRegulationAndFetchTemplates();

    this.trigger$ = merge(
      this.localizationService.changeDateFormatEvent,
      this.localizationService.changeLanguageEvent
    ).pipe(map(() => Math.random()), share());
  }

  public refreshTemplates(): void {
    this.fetchTemplates()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  public ngAfterViewInit(): void {
    this.filtersForm.valueChanges
      .pipe(
        debounceTime(100),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        switchMap(() => this.gloRegIdsOfRegTree?.length ? this.fetchTemplates() : of(EMPTY)),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public fetchTemplates(): Observable<{ data: ETemplate[], total: number }> {
    this.loading = true;

    let filters = `(gloReg.id=in=(${this.gloRegIdsOfRegTree}))`;
    if (this.isFiltering()) {
      filters += `;` + this.generateFilterQueryString({
        ...this.filtersForm.value,
        regulation: this.currentRegulationId,
        statuses: this.selectedStatuses
      });
    }

    return this.templateService.fetchTemplates(filters, this.pagination.currentPage.number, this.pagination.perPage)
      .pipe(
        debounceTime(600),
        tap(response => {
          this.templates = response.data;
          this.pagination.update(response.total);
        }),
        catchError(() => of({data: [], total: 0})),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
        takeUntil(this.componentDestroyed$)
      );
  }

  public trackByTemplateId(index: number, template: ETemplate): number {
    return template.id;
  }

  public onTemplateRowClick(templateId: number, event: Event) {
    const targetElement = <HTMLElement>event.target;
    const isContextMenuButton = targetElement.closest('button')?.classList?.contains('btn-context-menu');
    const isContextMenuItem = targetElement?.closest('li')?.classList?.contains('dropdown-item');

    if (!isContextMenuButton && !isContextMenuItem) {
      this.router.navigate(['templates', templateId]);
    }
  }

  public openPublishETemplateModal(eTemplateToPublish: ETemplate): void {
    this.selectedETemplate = {
      ...eTemplateToPublish,
      availability: eTemplateToPublish.availability ? [...eTemplateToPublish.availability] : []
    };
    this.initializePublishForm();

    this.modalService.openModalWithId('publishModal');
  }

  public getModalTitle(): string {
    if (this.isTemplatePublished()) {
      return this.translate.instant('TEMPLATE.unpublish_template_header');
    } else {
      return this.translate.instant('TEMPLATE.publish_template_header');
    }
  }

  public getModalConfirmationMessage(): string {
    if (this.isTemplatePublished()) {
      return this.translate.instant('TEMPLATE.unpublish_confirmation_message?', {
        selectedTemplateName: this.selectedETemplate?.name
      });
    } else {
      return this.translate.instant('TEMPLATE.publish_confirmation_message?', {
        selectedTemplateName: this.selectedETemplate?.name
      });
    }
  }

  public confirmDeleteETemplate(eTemplate: ETemplate): void {
    from(this.modalService.confirm({
      'title': 'DIALOG.delete_template',
      'text': `${this.translate.instant('DIALOG.confirm_delete_template?',
        {
          templateName: eTemplate.name
        })}`,
      'size': 'sm',
      'customClass': 'margin-top',
      'type': 'danger'
    })).pipe(
      switchMap(confirm => confirm ? this.templateService.deleteETemplate(eTemplate?.id) : EMPTY),
      tap(() => {
        this.toastService.success(this.translate.instant(`TEMPLATE.deleted_successfully`));
      }),
      switchMap(() => this.fetchTemplates()),
      catchError((errorResponse: HttpErrorResponse) => {
        const errorMessage = `${this.translate.instant(`TEMPLATE.deletion_failed`)}: ${errorResponse?.error?.message}`;
        this.toastService.error(errorMessage);
        return EMPTY;
      }),
      takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  public openCreateTemplateModal(): void {
    this.modalService.openModalWithName('TemplateActionModalComponent')
      .then((modelRef: ComponentRef<TemplateActionModalComponent>) => {
        modelRef.instance.regulations = this.regulations;
        modelRef.instance.createTemplate = true;
        modelRef.instance.templateCreated = this.templateCreated.bind(this);
      });
  }

  public openEditItemModal(eTemplateToEdit: ETemplate): void {
    this.modalService.openModalWithName('TemplateActionModalComponent')
      .then((modelRef: ComponentRef<TemplateActionModalComponent>) => {
        modelRef.instance.regulations = this.regulations;
        modelRef.instance.selectedTemplate = eTemplateToEdit;
        modelRef.instance.editTemplate = true;
        modelRef.instance.templateCreated = this.templateCreated.bind(this);
      });
  }

  public openDuplicateETemplateModal(eTemplateToDuplicate: ETemplate): void {
    this.modalService.openModalWithName('TemplateActionModalComponent')
      .then((modelRef: ComponentRef<TemplateActionModalComponent>) => {
        modelRef.instance.regulations = this.regulations;
        modelRef.instance.selectedTemplate = eTemplateToDuplicate;
        modelRef.instance.duplicateTemplate = true;
        modelRef.instance.templateCreated = this.templateCreated.bind(this);
      });
  }

  public templateCreated(id: number): void {
    this.pagination.currentPage.number = 0;
    this.newlyCreatedTemplateId = id;
    this.refreshTemplates();
  }

  public onPublish(): void {
    const publishRequestData = {
      name: this.selectedETemplate.name,
      locale: this.selectedETemplate.locale
    };
    if (this.isTemplatePublished()) {
      this.selectedETemplate.availability = [];
    } else {
      this.selectedETemplate.availability = ['HR_PRO'];
    }

    this.templateService.updateETemplate(this.selectedETemplate)
      .pipe(
        switchMap(() => this.templateService.publishETemplate(this.selectedETemplate.gloRegId, publishRequestData)),
        tap(() => {
          this.modalService.closeTopmostModal();
          this.toastService.success(this.translate.instant(`TEMPLATE.published_successfully`));
        }),
        switchMap(() => this.fetchTemplates()),
        catchError((errorResponse: HttpErrorResponse) => {
          const errorMessage = `${this.translate.instant(`TEMPLATE.publish_failed`)}: ${errorResponse?.error?.message}`;
          this.toastService.error(errorMessage);
          return EMPTY;
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public isAvailabilityChecked(available: string, availabilities: string[]): boolean {
    return availabilities !== undefined && availabilities?.includes(available);
  }

  public hasSignatureData(eTemplate: ETemplate): boolean {
    return (eTemplate?.signatureData?.length ?? 0) > 0;
  }

  public isFiltering() {
    return !isEmpty(Object.values(this.filtersForm.value)
      .filter(value => !!value)) || this.selectedStatuses?.size > 0 || this.currentRegulationId > 0;
  }

  public initStatusTree(): void {
    const treeRoot = new TreeItem('root');
    this.statusTree = [];

    treeRoot.items = [...this.statusList].map((status: StatusVM) =>
      new TreeItem(status.name, status.id, [`${status.select}`]));

    this.statusTree = treeRoot.items;
    this.cdr.markForCheck();
  }

  public onStatusSelected(ids: number[]): void {
    if (!this.templates) {
      return;
    }
    this.selectedStatuses.clear();

    this.statusList
      .filter(status => ids.indexOf(status.id) !== -1)
      .forEach(status => this.selectedStatuses.add(status.select));

    this.refreshTemplates();
  }

  public onRegulationSelected(regEntity: any) {
    this.currentRegulationId = regEntity.id;
  }

  public generateFilterQueryString(filter?: {
    name: string,
    folder: string,
    regulation: string,
    locale: string,
    createdBy: string,
    date: string,
    statuses: Set<string>
  }): string {
    const filterParam = [];

    if (filter?.name) {
      filterParam.push(`(name=='*${escapeValue(filter.name)}*')`);
    }
    if (filter.folder) {
      filterParam.push(`(folder=='*${escapeValue(filter.folder)}*')`);
    }
    if (filter.regulation) {
      filterParam.push(`(gloReg.id=in=(${escapeValue(filter.regulation)}))`);
    }
    if (filter.locale) {
      filterParam.push(`(locale=='*${escapeValue(filter.locale)}*')`);
    }
    if (filter.createdBy) {
      filterParam.push(`(createdBy=='*${escapeValue(filter.createdBy)}*')`);
    }
    if (filter.date) {
      const fromDate = moment(filter.date).startOf('day').utc(true).toISOString();
      const toDate = endOfDay(new Date(filter.date), true).toISOString();
      filterParam.push(`(updateDate=gt=${fromDate};updateDate=le=${toDate})`);
    }

    if (filter?.statuses?.size > 0) {
      filterParam.push(`status=in=(${(Array.from(filter.statuses).join(',').toUpperCase())})`);
    }

    return filterParam.join(';');
  }

  public clearAllFilters(): void {
    if (this.isFiltering()) {
      this.initStatusTree();
      this.selectedStatuses.clear();
      this.currentRegulationId = 0;

      this.pagination.currentPage.number = 0;
      this.filtersForm.setValue({name: '', folder: '', regulation: 0, locale: '', createdBy: '', date: ''});
    }
  }

  private initializePublishForm(): void {
    this.publishForm = this.fb.group({
      availabilities: new UntypedFormArray([])
    });

  }

  private getRegulationAndFetchTemplates() {
    this.administrationService.getCliRegData()
      .pipe(
        tap((result) => {
          this.regulations = result.data;
          this.gloRegIdsOfRegTree = result.data.map(cliRegData => cliRegData.attributes.gloReg);

          const takeGloRegIdAsValue = true;
          this.regulationTree = this.regulationTreeService.createTree(this.regulations, takeGloRegIdAsValue);

          if (this.currentRegulationId) {
            this.onRegulationSelected({id: this.currentRegulationId});
          }
        }),
        switchMap(() => this.fetchTemplates()),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  private isTemplatePublished(): boolean {
    if (this.selectedETemplate?.status === 'PUBLISHED') {
      return true;
    } else if (this.selectedETemplate?.status === 'DRAFT' || this.selectedETemplate?.status === 'UNPUBLISHED') {
      return false;
    }
  }
}
